import React from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import KpiStatCard from "../../../KpiDashboard/components/KpiStatCard";
import GaugeWidget from "../components/GaugeWidget";
import UnavailablePanel from "../components/UnavailablePanel";
import type { Availability, FinancialAnalytics, SavingsBreakdownRow } from "../../../../types/targetGroupAnalytics";
import { formatMoney } from "../utils/format";

interface FinancialImpactSectionProps {
  financial: Availability<FinancialAnalytics>;
}

/** US4 — financial impact snapshot, with graceful degradation (FR-013, FR-014). */
const FinancialImpactSection: React.FC<FinancialImpactSectionProps> = ({ financial }) => {
  if (financial.state === "unavailable") {
    return <UnavailablePanel reason={financial.reason} />;
  }
  if (financial.state === "empty" || !financial.data) {
    return (
      <div
        className="flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: "40vh" }}
      >
        <i className="pi pi-inbox text-5xl text-color-secondary mb-3" />
        <p className="text-color-secondary">No financial data in this scope yet.</p>
      </div>
    );
  }

  const data = financial.data;

  const amountBody = (row: SavingsBreakdownRow) => formatMoney(row.amount);

  return (
    <div className="flex flex-column gap-4">
      {data.isCurrentMonth && (
        <Message severity="info" text="Current month data is live and may change as more sessions complete." className="w-full" />
      )}

      {/* KPIs */}
      <div className="grid">
        <div className="col-12 md:col-3">
          <KpiStatCard
            title="Total money saved"
            value={formatMoney(data.moneySavedTotal)}
            icon="pi pi-euro"
            severity="info"
          />
        </div>
        <div className="col-12 md:col-3">
          <KpiStatCard
            title="Avg saved per member"
            value={formatMoney(data.averageSavedPerMember)}
            icon="pi pi-user"
            severity="info"
          />
        </div>
        <div className="col-12 md:col-3">
          <KpiStatCard
            title="Members with sessions"
            value={data.distinctMemberCount}
            icon="pi pi-users"
            severity="info"
          />
        </div>
        <div className="col-12 md:col-3">
          <KpiStatCard
            title="Target-group sessions"
            value={data.targetGroupSessionCount}
            icon="pi pi-bolt"
            severity="info"
          />
        </div>
      </div>

      {/* CDR share gauge */}
      <div className="grid">
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <GaugeWidget title="% of CDRs that are target-group CDRs" ratio={data.targetGroupCdrShare} />
          </Card>
        </div>
        {(data.negativeSavingSessions > 0 || data.excludedNoBaselineCount > 0) && (
          <div className="col-12 md:col-6">
            <Card className="h-full">
              <div className="flex flex-column gap-3 p-2">
                <span className="font-semibold text-sm text-800">Data quality</span>
                {data.negativeSavingSessions > 0 && (
                  <div className="flex justify-content-between text-sm">
                    <span className="text-color-secondary">Negative-saving sessions</span>
                    <span className="font-medium">{data.negativeSavingSessions} ({formatMoney(data.negativeSavingAmount)})</span>
                  </div>
                )}
                {data.excludedNoBaselineCount > 0 && (
                  <div className="flex justify-content-between text-sm">
                    <span className="text-color-secondary">Excluded (no baseline)</span>
                    <span className="font-medium">{data.excludedNoBaselineCount}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Breakdown tables */}
      <div className="grid">
        <div className="col-12 lg:col-6">
          <Card title="Savings by group">
            <DataTable
              value={data.savingsByGroup}
              size="small"
              sortField="amount.amount"
              sortOrder={-1}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              emptyMessage="No group savings recorded."
              rowHover
            >
              <Column field="label" header="Group" sortable style={{ minWidth: "200px" }} />
              <Column field="amount" header="Saved" body={amountBody} sortable style={{ minWidth: "140px" }} />
              <Column field="sessionCount" header="Sessions" sortable style={{ minWidth: "110px" }} />
            </DataTable>
          </Card>
        </div>
        <div className="col-12 lg:col-6">
          <Card title="Savings by member">
            <DataTable
              value={data.savingsByMember}
              size="small"
              sortField="amount.amount"
              sortOrder={-1}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              emptyMessage="No member savings recorded."
              rowHover
            >
              <Column field="label" header="Member" sortable style={{ minWidth: "200px" }} />
              <Column field="amount" header="Saved" body={amountBody} sortable style={{ minWidth: "140px" }} />
              <Column field="sessionCount" header="Sessions" sortable style={{ minWidth: "110px" }} />
            </DataTable>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FinancialImpactSection;
